import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Package, Truck, MapPin, CheckCircle2, AlertTriangle, Clock, Loader2, Activity,
} from "lucide-react";

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        <Icon size={18} style={{ color: accent }} />
      </div>
      <div className="mt-2 font-head text-3xl font-black text-[#0B192C]">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
    refetchInterval: 15000,
  });

  if (isLoading || !data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FF6500]" /></div>;

  return (
    <div className="animate-slide-up">
      <h1 className="font-head text-2xl font-extrabold text-[#0B192C]">{user?.role === "admin" ? "Admin Command Centre" : "Dispatch Dashboard"}</h1>
      <p className="text-sm text-slate-500">Live delivery operations overview</p>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Today" value={data.today_total} icon={Package} accent="#0B192C" />
        <Kpi label="Pending" value={data.pending} icon={Clock} accent="#F59E0B" />
        <Kpi label="In Transit" value={data.in_transit} icon={Truck} accent="#2563EB" />
        <Kpi label="Arrived" value={data.arrived} icon={MapPin} accent="#7C3AED" />
        <Kpi label="Completed" value={data.completed} icon={CheckCircle2} accent="#059669" />
        <Kpi label="Failed" value={data.failed} icon={AlertTriangle} accent="#DC2626" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Avg Time on Site</div>
          <div className="mt-1 font-head text-2xl font-black text-[#0B192C]">{data.avg_time_on_site} <span className="text-base font-semibold text-slate-400">min</span></div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Completed</div>
          <div className="mt-1 font-head text-2xl font-black text-emerald-600">{data.completed}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Still Pending</div>
          <div className="mt-1 font-head text-2xl font-black text-amber-600">{data.pending}</div>
        </div>
      </div>

      {/* Live deliveries */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4">
          <Activity size={18} className="text-[#FF6500]" />
          <h2 className="font-head font-bold text-[#0B192C]">Live Deliveries</h2>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="live-deliveries-table">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-5 py-3">Driver</th><th className="px-5 py-3">Order</th>
                <th className="px-5 py-3">Customer</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Time</th>
              </tr>
            </thead>
            <tbody>
              {data.live.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">No active deliveries right now</td></tr>}
              {data.live.map((l, i) => (
                <tr key={i} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" onClick={() => navigate("/admin/orders")}>
                  <td className="px-5 py-3 font-semibold text-[#0B192C]">{l.driver || "—"}</td>
                  <td className="px-5 py-3 font-mono text-[#FF6500]">#{l.order_number}</td>
                  <td className="px-5 py-3 text-slate-600">{l.customer}</td>
                  <td className="px-5 py-3"><StatusBadge status={l.status} /></td>
                  <td className="px-5 py-3 text-slate-500">{l.time != null ? `${l.time} min` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
